import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import { Loader2 } from "lucide-react";

import { StatusBadge } from "@/components/status-badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { describeMemberChangeKind, describeMemberChangeSource, roleLabel } from "./member-change-copy";

function describeRoles(row: { kind: string; previousRole: string | null; nextRole: string | null }) {
  if (row.kind === "joined") return `as ${roleLabel(row.nextRole)}`;
  if (row.kind === "removed") return `was ${roleLabel(row.previousRole)}`;
  return `${roleLabel(row.previousRole)} → ${roleLabel(row.nextRole)}`;
}

type KindFilter = "all" | "joined" | "removed" | "role_changed" | "ownership_transferred";

const KIND_FILTER_LABELS: Record<KindFilter, string> = {
  all: "All changes",
  joined: "Joined",
  removed: "Removed",
  role_changed: "Role changes",
  ownership_transferred: "Ownership",
};

/** Recent membership and role changes from every source, for owners and admins. */
export function MemberChangeHistoryCard() {
  const { data, isPending, error } = useQuery(convexQuery(api.members.listMemberChanges, {}));
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const rows = data?.filter((row) => kindFilter === "all" || row.kind === kindFilter) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Member activity</CardTitle>
        <CardDescription>Recent membership and role changes, newest first.</CardDescription>
        <div data-slot="card-action" className="col-start-2 row-span-2 row-start-1 self-start justify-self-end">
          <Select value={kindFilter} onValueChange={(value) => setKindFilter(value as KindFilter)}>
            <SelectTrigger className="w-40" aria-label="Filter activity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(KIND_FILTER_LABELS) as KindFilter[]).map((kind) => (
                <SelectItem key={kind} value={kind}>{KIND_FILTER_LABELS[kind]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-muted-foreground">Failed to load member activity.</p>
        ) : isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading activity...
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {kindFilter === "all" ? "No member changes yet." : "No changes of this kind yet."}
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row._id} className="flex items-start justify-between gap-3 rounded-md border px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {describeMemberChangeKind(row.kind)}: {row.targetEmail}{" "}
                    <span className="font-normal text-muted-foreground">{describeRoles(row)}</span>
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {new Date(row.createdAt).toLocaleString()} · {row.actorName ?? describeMemberChangeSource(row.source)}
                    {row.error ? ` · ${row.error}` : ""}
                  </p>
                </div>
                {row.status !== "completed" ? (
                  row.status === "failed" ? (
                    <StatusBadge status="error" label="Failed" />
                  ) : (
                    // Applied locally; waiting for WorkOS to confirm.
                    <StatusBadge status="pending" label="Pending" showSpinner={false} />
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
